import axios from "axios";
import AxiosAPI from "../../../helpers/AxiosAPI";
import Snippets from "../../../helpers/Snippets";

const PDF_CONVERTER_BASE = "https://hyperefficient2.net/sz-chat-gpt";
const DOWNLOAD_ATTACHMENT_BASE =
    (Snippets.urls.getAPIBaseURL?.() || "https://api-live.supplyzone.ai") +
    "/home/download-attachment/";

export const INTERNAL_ATTACHMENT_PREFIX = "sz-internal://attachment/";

const base64Cache = new Map<string, string>();
const MAX_BASE64_CACHE_ENTRIES = 12;

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp"];
const SUPPORTED_EXTENSIONS = [...IMAGE_EXTENSIONS, ".pdf"];

export type VisionContentPart =
    | { type: "text"; text: string }
    | { type: "image_url"; image_url: { url: string } };

export interface GPTUserMessage {
    role: "user";
    content: string | VisionContentPart[];
    userTriggered?: boolean;
    displayMeta?: {
        fileName?: string;
        attachmentPreview?: string;
        isInformalUpload?: boolean;
    };
}

export interface PdfQuoteEntry {
    s3Key: string;
    label: string;
}

const getExtension = (name: string): string => {
    const dot = name.lastIndexOf(".");
    return dot >= 0 ? name.slice(dot).toLowerCase() : "";
};

export const isImageFile = (name: string): boolean =>
    IMAGE_EXTENSIONS.includes(getExtension(name));

export const isPdfFile = (name: string): boolean =>
    getExtension(name) === ".pdf";

export const isSupportedVisionFile = (name: string): boolean =>
    SUPPORTED_EXTENSIONS.includes(getExtension(name));

const readFileAsDataURL = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

const blobToDataURL = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });

const buildVisionContent = (
    label: string,
    imageUrl: string,
    extraText?: string
): VisionContentPart[] => {
    const text = extraText ? `${extraText}\n${label}` : label;
    return [
        { type: "text", text },
        { type: "image_url", image_url: { url: imageUrl } },
    ];
};

export const fileToVisionMessage = async (
    file: File,
    label: string,
    extraText?: string
): Promise<GPTUserMessage> => {
    const dataUrl = await readFileAsDataURL(file);
    return {
        role: "user",
        content: buildVisionContent(label, dataUrl, extraText),
        displayMeta: {
            fileName: file.name,
            isInformalUpload: true,
        },
    };
};

const uploadPdfForConversion = async (file: File): Promise<string> => {
    const dataUrl = await readFileAsDataURL(file);
    const payload = {
        name: file.name,
        attachmentType: "OTHER",
        size: file.size,
        type: file.type,
        data: dataUrl,
        entityId: 0,
        isPrimary: false,
    };

    const res = await AxiosAPI.saveBusinessAttachment(payload);
    const s3Key =
        res.data?.s3Key ||
        res.data?.key ||
        res.data?.attachmentKey ||
        res.data?.documentKey;

    if (!s3Key) {
        throw new Error("Upload succeeded but no storage key was returned.");
    }

    return s3Key;
};

const resolveLabelForPng = (
    pngFile: string,
    entries: PdfQuoteEntry[]
): string => {
    const normalized = pngFile.toLowerCase();

    for (const entry of entries) {
        const keyLower = entry.s3Key.toLowerCase();
        const keyBase = keyLower.replace(/\.pdf$/, "");
        if (
            normalized.includes(keyLower) ||
            normalized.includes(keyBase) ||
            normalized.replace(/\.png$/, ".pdf") === keyLower
        ) {
            return entry.label;
        }
    }

    if (entries.length === 1) {
        return entries[0].label;
    }

    return entries[0]?.label || "Quotation";
};

export const convertS3KeysToVisionMessages = async (
    entries: PdfQuoteEntry[]
): Promise<GPTUserMessage[]> => {
    if (entries.length === 0) {
        return [];
    }

    const response = await axios.get(
        `${PDF_CONVERTER_BASE}/pdf_to_png.php?key=${entries
            .map((e) => e.s3Key)
            .join(",")}`
    );

    const pngFiles: string[] = Array.isArray(response.data)
        ? response.data
        : [];

    return pngFiles.map((pngFile, index) => {
        const baseLabel = resolveLabelForPng(pngFile, entries);
        const pageLabel =
            pngFiles.length > 1
                ? `${baseLabel} (page ${index + 1})`
                : baseLabel;

        return {
            role: "user" as const,
            content: buildVisionContent(
                pageLabel,
                `${PDF_CONVERTER_BASE}/pdf_to_png/${pngFile}`
            ),
            displayMeta: {
                fileName: pngFile,
            },
        };
    });
};

export const pdfToVisionMessages = async (
    file: File,
    label: string,
    extraText?: string
): Promise<{ messages: GPTUserMessage[]; tempS3Key?: string }> => {
    const s3Key = await uploadPdfForConversion(file);
    const entries: PdfQuoteEntry[] = [{ s3Key, label }];
    const messages = await convertS3KeysToVisionMessages(entries);

    if (messages.length === 0) {
        throw new Error("PDF conversion produced no images.");
    }

    if (extraText && messages[0].content && Array.isArray(messages[0].content)) {
        const textPart = messages[0].content.find((p) => p.type === "text");
        if (textPart && textPart.type === "text") {
            textPart.text = `${extraText}\n${textPart.text}`;
        }
    }

    messages.forEach((msg) => {
        msg.displayMeta = {
            ...msg.displayMeta,
            fileName: file.name,
            isInformalUpload: true,
        };
    });

    return { messages, tempS3Key: s3Key };
};

export const cleanupTempUpload = (s3Key?: string): void => {
    if (!s3Key) {
        return;
    }
    AxiosAPI.removeFileAttachment(s3Key).catch(() => {});
};

const fetchS3KeyAsDataUrl = async (s3Key: string): Promise<string> => {
    const token = Snippets.user.getCurrentUserAccessToken();
    const response = await axios.get(`${DOWNLOAD_ATTACHMENT_BASE}${s3Key}`, {
        responseType: "blob",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return blobToDataURL(response.data);
};

const cacheDataUrl = (s3Key: string, dataUrl: string): void => {
    if (base64Cache.size >= MAX_BASE64_CACHE_ENTRIES) {
        const oldestKey = base64Cache.keys().next().value;
        if (oldestKey) {
            base64Cache.delete(oldestKey);
        }
    }
    base64Cache.set(s3Key, dataUrl);
};

export const createSupplierImageReference = (
    s3Key: string,
    label: string,
    fileName?: string
): GPTUserMessage => ({
    role: "user",
    content: buildVisionContent(
        label,
        `${INTERNAL_ATTACHMENT_PREFIX}${s3Key}`
    ),
    displayMeta: { fileName: fileName || s3Key },
});

export const resolveMessagesForApi = async (
    messages: Array<{ role: string; content: any }>
): Promise<Array<{ role: string; content: any }>> => {
    const resolved: Array<{ role: string; content: any }> = [];

    for (const msg of messages) {
        if (!Array.isArray(msg.content)) {
            resolved.push({ role: msg.role, content: msg.content });
            continue;
        }

        const resolvedContent: VisionContentPart[] = [];
        for (const part of msg.content as VisionContentPart[]) {
            if (
                part.type === "image_url" &&
                part.image_url.url.startsWith(INTERNAL_ATTACHMENT_PREFIX)
            ) {
                const s3Key = part.image_url.url.slice(
                    INTERNAL_ATTACHMENT_PREFIX.length
                );
                let dataUrl = base64Cache.get(s3Key);
                if (!dataUrl) {
                    dataUrl = await fetchS3KeyAsDataUrl(s3Key);
                    cacheDataUrl(s3Key, dataUrl);
                }
                resolvedContent.push({
                    type: "image_url",
                    image_url: { url: dataUrl },
                });
            } else {
                resolvedContent.push(part);
            }
        }

        resolved.push({ role: msg.role, content: resolvedContent });
    }

    return resolved;
};

export const s3KeyImageToVisionMessage = async (
    s3Key: string,
    fileName: string,
    label: string
): Promise<GPTUserMessage> => {
    const dataUrl = await fetchS3KeyAsDataUrl(s3Key);

    return {
        role: "user",
        content: buildVisionContent(label, dataUrl),
        displayMeta: { fileName },
    };
};

export const s3KeyToVisionMessages = async (
    s3Key: string,
    fileName: string,
    label: string
): Promise<GPTUserMessage[]> => {
    if (isPdfFile(fileName)) {
        return convertS3KeysToVisionMessages([{ s3Key, label }]);
    }

    if (isImageFile(fileName)) {
        const message = await s3KeyImageToVisionMessage(s3Key, fileName, label);
        return [message];
    }

    return [];
};

export const fileToVisionMessages = async (
    file: File,
    label: string,
    extraText?: string
): Promise<{ messages: GPTUserMessage[]; tempS3Key?: string }> => {
    if (isPdfFile(file.name)) {
        return pdfToVisionMessages(file, label, extraText);
    }

    if (isImageFile(file.name)) {
        const message = await fileToVisionMessage(file, label, extraText);
        return { messages: [message] };
    }

    throw new Error(
        "Unsupported file type. Please upload a PNG, JPG, WEBP, or PDF quotation."
    );
};

export const INFORMAL_UPLOAD_LABEL = (fileName: string): string =>
    `Informal quote uploaded by buyer (not an official RFQ submission): ${fileName}`;

export const SUPPLIER_QUOTE_LABEL = (senderCompany: string): string =>
    `Quotation from ${senderCompany}.`;
